import { callApi } from "apiServices/callApi";
import { apiRoutes } from "constants/apiRoutes";
import { environments } from "constants/environments";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Login from "views/auth/login";
import './../assets/css/Auth.css';

// Define interfaces
interface User {
  apisList: ApisList;
  [key: string]: any;
}
interface ApisList {
  Dashboard: any;
  WebSite: any;
}
interface AuthContextType {
  userLogin: (token: string, data: User) => void;
  userLogout: () => void;
  sharedData: User | null;
  setSharedData: (data: User | null) => void;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [loggedIn, setLoggedIn] = useState<boolean>(!!localStorage.getItem(environments.tokenKey));
  const [sharedData, setSharedData] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const userLogin = (token: string, data: User) => {
    localStorage.setItem(environments.tokenKey, token);
    setLoggedIn(true);
    const { apisList, ...other } = data;
    const shared = { apisList, user: other };
    setSharedData(shared);
    navigate('/admin');
  };

  const userLogout = () => {
    setSharedData(null);
    setLoggedIn(false);
    localStorage.removeItem(environments.tokenKey);
    navigate('/auth/login');
  };

  const checkAdmin = async () => {
    const token = localStorage.getItem(environments.tokenKey);
    if (token) {
      try {
        setLoading(true);
        const url = apiRoutes.auth.checkAdmin;
        const response = await callApi("POST", url, {}, {}, {});
        if (response.Status === 200) {
          const shared = {
            apisList: response.Data.Content.apisList,
            user: response.Data.Content.existingAdmin,
          };
          setSharedData(shared);
          setLoggedIn(true);
          if (window.location.pathname !== '/admin') {
            navigate('/admin');
          }
        } else {
          userLogout();
        }
      } catch (error) {
        setError("Failed to check admin.");
        userLogout();
      } finally {
        setLoading(false);
      }
    } else {
      userLogout();
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAdmin();
  }, []);

  const authContextValue = useMemo(
    () => ({ userLogin, userLogout, sharedData, setSharedData }),
    [sharedData]
  );

  return (
    <AuthContext.Provider value={authContextValue}>
      {loading ?
       <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh'}}>
          <div className="spinner">
          <div></div>
          <div></div>
          <div></div>
          <div></div>
          <div></div>
        </div>
       </div> 
      : loggedIn ? children : <Login />}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
